import { ChangeEvent, forwardRef } from "react";
import { useNumberMask, useDecimalMask } from "@talladega/hooks";
import { BaseInputProps } from "@talladega/types";
import { Form } from ".";

type Props = {
  useDecimal?: boolean;
  onChange?: (value: number) => void;
  value?: number;
  defaultValue?: number;
  maxLength?: number;
  locale?: string;
} & Omit<BaseInputProps, "onChange" | "defaultValue">;

export const NumberInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      useDecimal,
      onChange,
      value,
      defaultValue,
      maxLength,
      locale = "en",
      disabled,
      ...props
    },
    ref
  ) => {
    const {
      maskInstance: maskNumber,
      maskPipe: numberMaskPipe,
      unMaskPipe: numberUnMaskPipe,
    } = useNumberMask();
    const {
      maskInstance: maskDecimal,
      maskPipe: decimalMaskPipe,
      unMaskPipe: decimalUnMaskPipe,
    } = useDecimalMask();

    const unmask = (event: ChangeEvent<HTMLInputElement>) => {
      const { value: v } = event.currentTarget;

      if (useDecimal) {
        maskDecimal.reset();
        maskDecimal.append(v, { input: true }, "");
        // eslint-disable-next-line no-param-reassign
        event.target.value = maskDecimal.value;
      } else {
        maskNumber.reset();
        maskNumber.append(v, { input: true }, "");
        // eslint-disable-next-line no-param-reassign
        event.target.value = maskNumber.value;
      }

      const unmaskedValue = useDecimal
        ? decimalUnMaskPipe(v)
        : numberUnMaskPipe(v);

      if (onChange) {
        onChange(unmaskedValue);
      }
    };

    const getValue = (valueToCheck: number | undefined | null) => {
      if (valueToCheck === undefined || valueToCheck === null) return undefined;

      return useDecimal
        ? decimalMaskPipe(valueToCheck?.toString(), locale)
        : numberMaskPipe(valueToCheck?.toString(), locale);
    };

    return (
      <Form.Input
        ref={ref}
        value={getValue(value)}
        defaultValue={getValue(defaultValue)}
        maxLength={maxLength}
        onChange={unmask}
        disabled={disabled}
        {...props}
      />
    );
  }
);

export default {};
